import { ConsentChannel, ConsentStatusDTO, ConsentType } from 'models/consent'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'

import { AxiosResponse } from 'axios'
import { RoleMimetype } from 'constants/API'
import { useQuery } from '@tanstack/react-query'
import API from 'utils/API/API'
import { useAPI } from 'utils/API'

enum Endpoints {
  CONSENT = '/consent',
}

/**
 * Fetches the authenticated user's current consent status for an integration channel (ISSUE-2012).
 * Backend: `GET /consent?workspaceId=...&channel=...` (client-only endpoint).
 */
export function useGetConsentStatus(workspaceId: string, channel: ConsentChannel, enabled: boolean) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.CONSENT, QueryType.GET, { workspaceId, channel }],
    queryFn: () => api.get<ConsentStatusDTO[]>(
      Endpoints.CONSENT,
      {},
      false,
      { params: { workspaceId, channel } }
    ),
    enabled: enabled && !!workspaceId,
  })
}

/** Payload for recording the user's acceptance of consent documents. */
export interface RecordConsentPayload {
  /** Workspace the consent is recorded in. The authenticated user must be a member. */
  workspaceId: string
  /** Integration channel the consent applies to. */
  channel: ConsentChannel
  /** The consent document types the user is accepting (versions are stamped server-side). */
  consentTypes: ConsentType[]
}

/**
 * Records the user's acceptance of consent documents (ISSUE-2012). The backend stamps the
 * currently-required document versions server-side.
 *
 * A concurrent duplicate submit surfaces as HTTP 409 ("Consent already recorded") — benign for
 * callers (the winning request persisted the acceptance), hence excluded from Sentry reporting.
 * The consent endpoints are client-only, so the Accept header is pinned to the client mimetype.
 */
export function useRecordConsent() {
  return getMutation<AxiosResponse<ConsentStatusDTO[]>, RecordConsentPayload>(
    (payload) => API.post(
      Endpoints.CONSENT,
      payload,
      { headers: { Accept: RoleMimetype.CLIENT } },
      {
        endpoint: Endpoints.CONSENT,
        ignoreErrorFactory: (error) => error.response?.status === 409,
      }
    ),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.CONSENT] })
    }
  )
}
